import { DRIVE } from './domain/driveModel';

/** Dosyasız Web Audio: hıza bağlı elektrikli motor uğultusu ve kısa efektler. Telefon hoparlörü için bas seslere orta frekans gövdesi eklenir. */
export type RallySound = 'start' | 'lane' | 'coin' | 'obstacle' | 'correct' | 'wrong' | 'finish';

const STORAGE_KEY = 'lane-rally-muted';
const MASTER_GAIN = 0.55;

export class RallyAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private engine: OscillatorNode[] = [];
  private noiseBuffer: AudioBuffer | null = null;
  private muted = false;

  constructor() { try { this.muted = localStorage.getItem(STORAGE_KEY) === 'true'; } catch { /* Depolama isteğe bağlı. */ } }
  isMuted() { return this.muted; }

  unlock() {
    if (!this.context) {
      const context = new AudioContext(); this.context = context;
      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -12; compressor.ratio.value = 10; compressor.attack.value = 0.003; compressor.release.value = 0.2;
      this.master = context.createGain(); this.master.gain.value = this.muted ? 0 : MASTER_GAIN;
      this.master.connect(compressor).connect(context.destination);
      this.engineFilter = context.createBiquadFilter(); this.engineFilter.type = 'lowpass'; this.engineFilter.frequency.value = 600; this.engineFilter.Q.value = 3;
      this.engineGain = context.createGain(); this.engineGain.gain.value = 0;
      this.engineFilter.connect(this.engineGain).connect(this.master);
      // Elektrikli motor: iki testere + orta frekans gövdesi (küçük hoparlörde de duyulur).
      for (const [type, ratio, level] of [['sawtooth', 1, 0.5], ['square', 1.5, 0.22], ['sine', 4, 0.35]] as const) {
        const oscillator = context.createOscillator(); oscillator.type = type; oscillator.frequency.value = 60 * ratio;
        const gain = context.createGain(); gain.gain.value = level; oscillator.connect(gain).connect(this.engineFilter); oscillator.start();
        oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
        this.engine.push(oscillator);
      }
      const buffer = context.createBuffer(1, context.sampleRate * 0.6, context.sampleRate);
      const data = buffer.getChannelData(0); for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
    }
    void this.context.resume().catch(() => undefined);
  }

  toggle() {
    this.muted = !this.muted;
    try { localStorage.setItem(STORAGE_KEY, String(this.muted)); } catch { /* isteğe bağlı */ }
    if (this.master && this.context) this.master.gain.setTargetAtTime(this.muted ? 0 : MASTER_GAIN, this.context.currentTime, 0.05);
    return this.muted;
  }
  pause() { void this.context?.suspend().catch(() => undefined); }
  resume() { if (this.context) void this.context.resume().catch(() => undefined); }

  /** Her karede: hız (m/sn) ve gaz durumu motor perdesini ve yüksekliğini belirler. */
  drive(speed: number, throttle: boolean) {
    if (!this.context || !this.engineGain || !this.engineFilter) return;
    const now = this.context.currentTime, ratio = Math.min(1.4, speed / DRIVE.cruiseSpeed);
    const base = 48 + ratio * 95;
    [1, 1.5, 4].forEach((factor, i) => this.engine[i]?.frequency.setTargetAtTime(base * factor, now, 0.08));
    this.engineFilter.frequency.setTargetAtTime(420 + ratio * 1500 + (throttle ? 300 : 0), now, 0.1);
    this.engineGain.gain.setTargetAtTime(speed < 0.2 && !throttle ? 0.018 : 0.05 + ratio * 0.07 + (throttle ? 0.02 : 0), now, 0.12);
  }

  private tone(frequency: number, at: number, duration: number, type: OscillatorType, gain: number, endFrequency?: number) {
    const context = this.context!; const oscillator = context.createOscillator(), amp = context.createGain();
    const start = context.currentTime + at;
    oscillator.type = type; oscillator.frequency.setValueAtTime(frequency, start);
    if (endFrequency) oscillator.frequency.exponentialRampToValueAtTime(endFrequency, start + duration);
    amp.gain.setValueAtTime(0, start); amp.gain.linearRampToValueAtTime(gain, start + 0.012); amp.gain.exponentialRampToValueAtTime(0.001, start + duration);
    oscillator.connect(amp).connect(this.master!); oscillator.start(start); oscillator.stop(start + duration + 0.02);
    oscillator.onended = () => { oscillator.disconnect(); amp.disconnect(); };
  }

  private noise(at: number, duration: number, type: BiquadFilterType, frequency: number, gain: number) {
    if (!this.noiseBuffer) return;
    const context = this.context!; const source = context.createBufferSource(), filter = context.createBiquadFilter(), amp = context.createGain();
    const start = context.currentTime + at;
    source.buffer = this.noiseBuffer; filter.type = type; filter.frequency.value = frequency;
    amp.gain.setValueAtTime(gain, start); amp.gain.exponentialRampToValueAtTime(0.001, start + duration);
    source.connect(filter).connect(amp).connect(this.master!); source.start(start); source.stop(start + duration + 0.02);
    source.onended = () => { source.disconnect(); filter.disconnect(); amp.disconnect(); };
  }

  play(sound: RallySound) {
    if (!this.context || !this.master || this.muted) return;
    if (sound === 'lane') { this.noise(0, 0.16, 'bandpass', 1400, 0.09); return; }
    if (sound === 'coin') { this.tone(1319, 0, 0.12, 'square', 0.07); this.tone(1976, 0.07, 0.22, 'square', 0.07); return; }
    if (sound === 'obstacle') { this.tone(130, 0, 0.35, 'triangle', 0.3, 55); this.tone(390, 0, 0.18, 'square', 0.08, 180); this.noise(0, 0.3, 'lowpass', 900, 0.4); return; }
    if (sound === 'correct') { [523, 659, 784, 1047].forEach((f, i) => this.tone(f, i * 0.07, 0.3, 'triangle', 0.14)); this.noise(0.05, 0.6, 'highpass', 2500, 0.08); return; }
    if (sound === 'wrong') { this.tone(311, 0, 0.3, 'sawtooth', 0.08, 233); this.tone(233, 0.2, 0.45, 'sawtooth', 0.08, 155); this.noise(0, 0.7, 'bandpass', 700, 0.12); return; }
    if (sound === 'start') { [0, 0.18, 0.36].forEach((at) => this.tone(660, at, 0.12, 'square', 0.08)); this.tone(1320, 0.54, 0.35, 'square', 0.1); return; }
    [523, 659, 784, 1047, 1319].forEach((f, i) => this.tone(f, i * 0.1, 0.45, 'triangle', 0.13));
  }

  dispose() {
    this.engine.forEach((oscillator) => { try { oscillator.stop(); } catch { /* zaten durdu */ } });
    this.engine = [];
    void this.context?.close().catch(() => undefined);
    this.context = null; this.master = null; this.engineGain = null; this.engineFilter = null; this.noiseBuffer = null;
  }
}
